// =============================================
// شريط التنقل الرئيسي - DATX
// الأسماء: الرئيسية | المسارات | الدورات | الندوات | خدماتنا | من نحن
// =============================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, KeyRound } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { StudentLoginModal } from './StudentLoginModal';
import { StudentProfileDropdown } from './StudentProfileDropdown';
import { useSettings } from '../context/SettingsContext';
import { formatImageUrl } from '../utils/imageUrl';

const formatLogoUrl = (url) => formatImageUrl(url, '/logo-full.png');
const formatAssetUrl = formatLogoUrl;

// =============================================
// المكون الرئيسي - شريط التنقل
// =============================================
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isStudentLoginOpen, setIsStudentLoginOpen] = useState(false);
  const { settings } = useSettings();
  const navigate = useNavigate();

  const contactPhones = useMemo(() => {
    if (!settings?.contact_phones) return ['779299707'];
    try {
      const parsed = typeof settings.contact_phones === 'string' ? JSON.parse(settings.contact_phones) : settings.contact_phones;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : ['779299707'];
    } catch (e) {
      return [settings.contact_phones];
    }
  }, [settings]);

  const checkAuth = useCallback(() => {
    const name = localStorage.getItem('datx_user_name');
    const email = localStorage.getItem('datx_user_email');
    if (name || email) {
      setCurrentUser({
        name: name || 'طالب مسجل',
        email: email || '',
      });
    } else {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    window.addEventListener('datx-auth-changed', checkAuth);
    window.addEventListener('storage', checkAuth);
    return () => {
      window.removeEventListener('datx-auth-changed', checkAuth);
      window.removeEventListener('storage', checkAuth);
    };
  }, [checkAuth]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoutStudent = useCallback(() => {
    localStorage.removeItem('datx_user_name');
    localStorage.removeItem('datx_user_email');
    localStorage.removeItem('datx_user_phone');
    localStorage.removeItem('datx_user_registrations');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('datx_token');
    localStorage.removeItem('datx_student_token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    window.dispatchEvent(new CustomEvent('datx-auth-changed'));
  }, []);

  const handleWhatsAppClick = useCallback(() => {
    const phone = settings?.whatsapp_number || '779299707';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const finalPhone = cleanPhone.startsWith('967') ? cleanPhone : `967${cleanPhone}`;
    const msg = encodeURIComponent(settings?.whatsapp_default_message || 'مرحباً، أود الاستفسار عن برامج ودورات داتكس DATX.');
    window.open(`https://wa.me/${finalPhone}?text=${msg}`, '_blank', 'noopener,noreferrer');
  }, [settings]);

  const handleTracksClick = useCallback((e) => {
    e.preventDefault();
    navigate('/tracks');
  }, [navigate]);

  const closeMobileMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`bg-white border-b font-arabic fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-lg shadow-black/10' : ''
          }`}
      >


        {/* شريط التنقل الرئيسي */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center h-16 md:h-18 py-0">
            <div className="flex-shrink-0 flex items-center relative z-20">
              <Link
                to="/"
                className="flex items-center m-0 p-0 hover:opacity-95 transition-opacity"
                aria-label={settings?.site_name_ar || 'DATX'}
              >
                {settings?.show_header_logo !== 'false' ? (
                  <img
                    src={formatAssetUrl(settings?.site_logo)}
                    alt={settings?.site_name_ar || 'DATX - داتكس'}
                    width="160"
                    height="48"
                    fetchPriority="high"
                    decoding="async"
                    className="h-24 sm:h-28 md:h-32 w-auto max-w-none object-contain transition-all duration-300"
                    onError={(e) => {
                      e.currentTarget.src = '/logo-full.png';
                    }}
                  />
                ) : (
                  <span className="text-2xl sm:text-3xl font-black text-[#00131D]">
                    {settings?.site_name_ar || 'DATX'}
                  </span>
                )}
              </Link>
            </div>

            <div className="md:hidden flex items-center gap-2">
              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2.5 rounded-xl text-gray-600 hover:text-[#00131D] hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#00131D] transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={isOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
              >
                {isOpen ? (
                  <X className="block h-6 w-6" strokeWidth={2} />
                ) : (
                  <Menu className="block h-6 w-6" strokeWidth={2} />
                )}
              </motion.button>
            </div>

            {/* روابط التنقل */}
            <div className="hidden md:flex md:items-center md:space-x-6 md:rtl:space-x-reverse">
              <Link
                to="/"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
              >
                الرئيسية
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>

              <Link
                to="/tracks"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
                onClick={handleTracksClick}
              >
                المسارات
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>

              <Link
                to="/courses"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
              >
                الدورات
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>

              {/* الندوات - رابط جديد */}
              <Link
                to="/webinars"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
              >
                الندوات
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>

              {/* المدونة التعليمية */}
              <Link
                to="/blog"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
              >
                المدونة
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>

              <Link
                to="/services"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
              >
                خدماتنا
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>

              <Link
                to="/about"
                className="text-gray-700 hover:text-[#00131D] px-3 py-2 text-sm font-semibold transition-all duration-300 relative group"
              >
                من نحن
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-3">
              {currentUser ? (
                <StudentProfileDropdown currentUser={currentUser} onLogout={handleLogoutStudent} />
              ) : (
                <motion.button
                  onClick={() => setIsStudentLoginOpen(true)}
                  className="px-4 py-2.5 text-xs font-bold text-[#00131D] hover:text-blue-600 bg-gray-100 hover:bg-blue-50 rounded-xl transition-all duration-300 border border-gray-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  دخول الطلاب
                </motion.button>
              )}

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact"
                  className="relative overflow-hidden text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 hover:shadow-xl shadow-md group block"
                  style={{ backgroundColor: 'rgb(11, 47, 159)' }}
                >
                  <span className="relative z-10">تواصل معنا</span>
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Student Login Modal */}
      <StudentLoginModal
        isOpen={isStudentLoginOpen}
        onClose={() => setIsStudentLoginOpen(false)}
      />

      {/* القائمة المتنقلة */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-b overflow-hidden shadow-lg fixed top-[64px] left-0 right-0 z-50"
          >
            <div className="px-4 pt-2 pb-4 space-y-1">
              {currentUser && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs font-bold text-[#00131D]">{currentUser.name}</span>
                  </div>
                  <button
                    onClick={() => {
                      handleLogoutStudent();
                      closeMobileMenu();
                    }}
                    className="text-xs text-red-600 hover:underline font-bold"
                  >
                    تسجيل الخروج
                  </button>
                </div>
              )}
              <Link
                to="/"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                الرئيسية
              </Link>
              <Link
                to="/tracks"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                المسارات
              </Link>
              <Link
                to="/courses"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                الدورات
              </Link>
              <Link
                to="/webinars"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                الندوات
              </Link>
              <Link
                to="/blog"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                المدونة
              </Link>
              <Link
                to="/services"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                خدماتنا
              </Link>
              <Link
                to="/about"
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-[#00131D] hover:bg-blue-50 rounded-xl transition-all duration-200"
                onClick={closeMobileMenu}
              >
                من نحن
              </Link>

              {!currentUser && (
                <button
                  onClick={() => {
                    setIsStudentLoginOpen(true);
                    closeMobileMenu();
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-blue-50 text-[#00131D] border border-gray-200 px-4 py-3 rounded-xl text-base font-bold transition-all duration-200 mt-3"
                >
                  <KeyRound className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
                  <span>دخول الطلاب المسجلين</span>
                </button>
              )}

              <Link
                to="/contact"
                className="block w-full text-center text-white px-4 py-3.5 rounded-xl text-base font-bold transition-all duration-300 hover:shadow-lg mt-2"
                style={{ backgroundColor: 'rgb(11, 47, 159)' }}
                onClick={closeMobileMenu}
              >
                تواصل معنا
              </Link>

              <div className="mt-4 px-3 space-y-3">
                <motion.button
                  onClick={handleWhatsAppClick}
                  className="flex items-center gap-3 text-gray-600 hover:text-[#25D366] transition-colors w-full p-2 hover:bg-green-50 rounded-xl"
                  whileHover={{ x: 5 }}
                >
                  <FaWhatsapp className="w-5 h-5 text-[#25D366]" style={{ color: '#25D366' }} />
                  <span className="font-semibold">{settings?.whatsapp_number || contactPhones[0] || '779299707'}</span>
                </motion.button>
                <motion.a
                  href="mailto:info@datxsci.com"
                  className="flex items-center gap-3 text-gray-600 hover:text-[#00131D] transition-colors p-2 hover:bg-blue-50 rounded-xl"
                  whileHover={{ x: 5 }}
                >
                  <Mail className="w-5 h-5 text-blue-500" strokeWidth={1.75} />
                  <span>info@datxsci.com</span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;